import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, Save, RotateCcw, Eye, Edit3, History,
  Building2, Calendar, CheckCircle2, AlertTriangle,
  Users, Download, ChevronDown, ChevronUp
} from 'lucide-react';

export default function SoldierAgreementManager({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [history, setHistory] = useState([]);
  const [signedAgreements, setSignedAgreements] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const [expandedAgreement, setExpandedAgreement] = useState(null);

  // Edit form state
  const [editData, setEditData] = useState({
    version: '',
    effective_date: '',
    agreement_text: '',
    company_legal_name: '',
    company_dba: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_zip: ''
  });

  useEffect(() => {
    fetchTemplate();
    fetchSignedAgreements();
  }, []);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/soldier-agreement/admin/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data.current_template);
      setHistory(response.data.history || []);
      
      // Pre-fill edit form
      const t = response.data.current_template;
      setEditData({
        version: t.version,
        effective_date: t.effective_date,
        agreement_text: t.agreement_text,
        company_legal_name: t.company?.legal_name || '',
        company_dba: t.company?.dba || '',
        company_address: t.company?.address || '',
        company_city: t.company?.city || '',
        company_state: t.company?.state || '',
        company_zip: t.company?.zip || ''
      });
    } catch (error) {
      toast.error('Failed to load agreement template');
    } finally {
      setLoading(false);
    }
  };

  const fetchSignedAgreements = async () => {
    try {
      const response = await axios.get(`${API}/soldier-agreement/admin/agreements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSignedAgreements(response.data.agreements || []);
    } catch (error) {
      console.error('Failed to fetch signed agreements:', error);
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/soldier-agreement/admin/template`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Agreement template updated successfully');
      setEditMode(false);
      fetchTemplate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!confirm('Are you sure you want to reset to the default agreement template? This will deactivate any custom templates.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/soldier-agreement/admin/template/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Agreement reset to default template');
      fetchTemplate();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-amber-400 flex items-center gap-2">
          <FileText className="w-5 h-5 animate-pulse" />
          Loading Agreement Manager...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-amber-400" />
            Soldier Agreement Manager
          </h2>
          <p className="text-stone-400 text-sm mt-1">
            View, edit, and manage the Independent Contractor Agreement
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-500/20 text-green-400">
            Version {template?.version}
          </Badge>
          <Badge className="bg-blue-500/20 text-blue-400">
            <Users className="w-3 h-3 mr-1" />
            {signedAgreements.length} Signed
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="view" className="space-y-4">
        <TabsList className="bg-stone-800/50 border border-stone-700">
          <TabsTrigger value="view" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Eye className="w-4 h-4 mr-2" />
            View Agreement
          </TabsTrigger>
          <TabsTrigger value="edit" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
            <Edit3 className="w-4 h-4 mr-2" />
            Edit Agreement
          </TabsTrigger>
          <TabsTrigger value="signed" className="data-[state=active]:bg-green-500 data-[state=active]:text-black">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Signed Agreements
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
            <History className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* View Agreement Tab */}
        <TabsContent value="view">
          <Card className="bg-stone-900/50 border-stone-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Current Agreement Template</CardTitle>
                  <CardDescription className="text-stone-400">
                    Effective: {template?.effective_date}
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFullText(!showFullText)}
                  className="border-stone-600 text-stone-300"
                >
                  {showFullText ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                  {showFullText ? 'Collapse' : 'Expand Full Text'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Company Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-stone-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-amber-400" />
                    <span className="text-stone-400 text-sm">Company</span>
                  </div>
                  <p className="text-white font-medium">{template?.company?.legal_name}</p>
                  <p className="text-stone-400 text-sm">DBA {template?.company?.dba}</p>
                </div>
                <div className="p-4 bg-stone-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-amber-400" />
                    <span className="text-stone-400 text-sm">Details</span>
                  </div>
                  <p className="text-white font-medium">Version {template?.version}</p>
                  <p className="text-stone-400 text-sm">{template?.company?.address}, {template?.company?.city}, {template?.company?.state} {template?.company?.zip}</p>
                </div>
              </div>

              {/* Agreement Text */}
              <div className={`bg-stone-950 rounded-lg p-4 font-mono text-sm text-stone-300 overflow-hidden ${showFullText ? 'max-h-[600px] overflow-y-auto' : 'max-h-64'}`}>
                <pre className="whitespace-pre-wrap">{template?.agreement_text}</pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Edit Agreement Tab */}
        <TabsContent value="edit">
          <Card className="bg-stone-900/50 border-stone-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Edit Agreement Template</CardTitle>
                  <CardDescription className="text-stone-400">
                    Make changes to the agreement. A new version will be created.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetToDefault}
                    className="border-stone-600 text-stone-300"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reset to Default
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Version & Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-300">Version Number</Label>
                  <Input
                    value={editData.version}
                    onChange={(e) => setEditData({ ...editData, version: e.target.value })}
                    placeholder="1.1"
                    className="bg-stone-800 border-stone-600 text-white mt-1"
                  />
                  <p className="text-xs text-stone-500 mt-1">Leave blank to auto-increment</p>
                </div>
                <div>
                  <Label className="text-stone-300">Effective Date</Label>
                  <Input
                    value={editData.effective_date}
                    onChange={(e) => setEditData({ ...editData, effective_date: e.target.value })}
                    placeholder="January 18, 2026"
                    className="bg-stone-800 border-stone-600 text-white mt-1"
                  />
                </div>
              </div>

              {/* Company Info */}
              <div>
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-amber-400" />
                  Company Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-300">Legal Name</Label>
                    <Input
                      value={editData.company_legal_name}
                      onChange={(e) => setEditData({ ...editData, company_legal_name: e.target.value })}
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-300">DBA (Doing Business As)</Label>
                    <Input
                      value={editData.company_dba}
                      onChange={(e) => setEditData({ ...editData, company_dba: e.target.value })}
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-stone-300">Address</Label>
                    <Input
                      value={editData.company_address}
                      onChange={(e) => setEditData({ ...editData, company_address: e.target.value })}
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-300">City</Label>
                    <Input
                      value={editData.company_city}
                      onChange={(e) => setEditData({ ...editData, company_city: e.target.value })}
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-stone-300">State</Label>
                      <Input
                        value={editData.company_state}
                        onChange={(e) => setEditData({ ...editData, company_state: e.target.value })}
                        className="bg-stone-800 border-stone-600 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">ZIP</Label>
                      <Input
                        value={editData.company_zip}
                        onChange={(e) => setEditData({ ...editData, company_zip: e.target.value })}
                        className="bg-stone-800 border-stone-600 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Agreement Text */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-stone-300">Agreement Text</Label>
                  <span className="text-xs text-stone-500">{editData.agreement_text?.length || 0} characters</span>
                </div>
                <Textarea
                  value={editData.agreement_text}
                  onChange={(e) => setEditData({ ...editData, agreement_text: e.target.value })}
                  rows={20}
                  className="bg-stone-800 border-stone-600 text-white font-mono text-sm"
                  placeholder="Enter the full agreement text..."
                />
                <p className="text-xs text-stone-500 mt-1">
                  Use [SOLDIER LEGAL NAME] and [SOLDIER ADDRESS] as placeholders for personalization.
                </p>
              </div>

              {/* Warning */}
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-400 font-medium">Important Notice</p>
                    <p className="text-stone-400 text-sm mt-1">
                      Updating the agreement will create a new version. Soldiers who have already signed will keep their existing agreement. New soldiers will sign the updated version.
                    </p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save New Version
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Signed Agreements Tab */}
        <TabsContent value="signed">
          <Card className="bg-stone-900/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white">Signed Agreements</CardTitle>
              <CardDescription className="text-stone-400">
                {signedAgreements.length} soldiers have signed the agreement
              </CardDescription>
            </CardHeader>
            <CardContent>
              {signedAgreements.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  No signed agreements yet
                </div>
              ) : (
                <div className="space-y-3">
                  {signedAgreements.map((agreement) => (
                    <div
                      key={agreement.agreement_id}
                      className="p-4 bg-stone-800/50 rounded-lg border border-stone-700"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{agreement.legal_name}</p>
                            <p className="text-stone-400 text-sm">{agreement.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-stone-700 text-stone-300">v{agreement.version}</Badge>
                          <p className="text-stone-500 text-xs mt-1">
                            {new Date(agreement.signed_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      {/* Expandable Details */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedAgreement(expandedAgreement === agreement.agreement_id ? null : agreement.agreement_id)}
                        className="mt-3 text-stone-400 hover:text-white w-full justify-start"
                      >
                        {expandedAgreement === agreement.agreement_id ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-1" />
                            Hide Details
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-1" />
                            Show Details
                          </>
                        )}
                      </Button>
                      
                      {expandedAgreement === agreement.agreement_id && (
                        <div className="mt-3 pt-3 border-t border-stone-700 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-stone-500">Agreement ID</p>
                            <p className="text-white font-mono">{agreement.agreement_id}</p>
                          </div>
                          <div>
                            <p className="text-stone-500">Beneficiary</p>
                            <p className="text-white">{agreement.beneficiary_name} ({agreement.beneficiary_relationship})</p>
                          </div>
                          <div>
                            <p className="text-stone-500">Address</p>
                            <p className="text-white">{agreement.city}, {agreement.state}</p>
                          </div>
                          <div>
                            <p className="text-stone-500">Tax Classification</p>
                            <p className="text-white capitalize">{agreement.tax_classification?.replace('_', ' ')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="bg-stone-900/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white">Template History</CardTitle>
              <CardDescription className="text-stone-400">
                Previous versions of the agreement template
              </CardDescription>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  No version history available. Using default template.
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((item, idx) => (
                    <div
                      key={item.template_id || idx}
                      className={`p-4 rounded-lg border ${item.is_active ? 'bg-green-500/10 border-green-500/30' : 'bg-stone-800/50 border-stone-700'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">Version {item.version}</p>
                            {item.is_active && (
                              <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                            )}
                          </div>
                          <p className="text-stone-400 text-sm">Effective: {item.effective_date}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-stone-500 text-xs">
                            Created: {new Date(item.created_at).toLocaleDateString()}
                          </p>
                          <p className="text-stone-500 text-xs">
                            By: {item.created_by}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
